import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as HomeIcon } from "assets/images/house-regular-full 1.svg";
import { ReactComponent as LoansGridIcon } from "assets/images/v2/nav-desktop-home.svg";
import { ReactComponent as SettingsIcon } from "assets/images/v2/gear-icon.svg";
import { closeProfile } from "features/authSlice";
import { useLoanSidebarData } from "hooks/useLoanSidebarData";
import type { MobileBottomNavItem } from "components/v2/mobile-bottom-nav/MobileBottomNav";
import { trackFSNavShellBottomNavTabSelect, type BottomNavTabId } from "utilities/full-story";
import { getLoanCentricMobileDockActiveTab } from "./getLoanCentricMobileDockActiveTab";
import { LoanCentricMobileDockIds } from "./loanCentricMobileDockIds";

export interface UseLoanCentricMobileDockResult {
  readonly items: MobileBottomNavItem[];
  readonly activeTab: LoanCentricMobileDockIds;
  readonly onTabChange: (tab: string) => void;
}

export function useLoanCentricMobileDock(): UseLoanCentricMobileDockResult {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { portfolioLoanCount } = useLoanSidebarData();

  const activeTab = getLoanCentricMobileDockActiveTab({ pathname });

  const items = useMemo<MobileBottomNavItem[]>(
    () => [
      {
        id: LoanCentricMobileDockIds.Home,
        label: "Home",
        icon: <HomeIcon />,
        order: 0,
      },
      {
        id: LoanCentricMobileDockIds.Loans,
        label: "Loans",
        icon: <LoansGridIcon />,
        badge: portfolioLoanCount >= 1 ? portfolioLoanCount : null,
        order: 1,
      },
      {
        id: LoanCentricMobileDockIds.Settings,
        label: "Settings",
        icon: <SettingsIcon />,
        order: 2,
      },
    ],
    [portfolioLoanCount],
  );

  const onTabChange = (tab: string) => {
    trackFSNavShellBottomNavTabSelect({ tabId: tab as BottomNavTabId });

    // Close any legacy profile overlay before route navigation.
    dispatch(closeProfile());

    if (tab === LoanCentricMobileDockIds.Home) {
      navigate("/home");
      return;
    }

    if (tab === LoanCentricMobileDockIds.Loans) {
      navigate("/my-loans");
      return;
    }

    if (tab === LoanCentricMobileDockIds.Settings) {
      navigate("/settings");
    }
  };

  return { items, activeTab, onTabChange };
}
